import dagre from '@dagrejs/dagre';
import {
  GRAPH_COLUMN_SPACING,
  GRAPH_ROW_SPACING,
  LAYOUT_X_OFFSET_FACTOR,
  LAYOUT_Y_OFFSET_FACTOR,
  ROOT_NODE_OFFSET,
} from '../../playbook-builder.constants';
import type { InitialEdgeData, InitialNodeData } from '../parse-graph';
import { setNodeOrderWithinRanks } from './set-node-order-within-ranks';

type RootNodePosition = {
  x: number;
  y: number;
};

const getInitialRootNodePosition = (
  rootNodeFromInput: InitialNodeData | undefined,
  graph: dagre.graphlib.Graph<unknown>,
): RootNodePosition => {
  if (!rootNodeFromInput) {
    return { x: 0, y: 0 };
  }

  const rootNodeLayoutInfo = graph.node(rootNodeFromInput.id);
  if (rootNodeLayoutInfo) {
    return {
      x: rootNodeLayoutInfo.x,
      y: rootNodeLayoutInfo.y + ROOT_NODE_OFFSET,
    };
  }

  return { x: 0, y: 0 };
};

export type LayoutedNode = InitialNodeData & {
  position: {
    x: number;
    y: number;
  };
};

/** Use dagre to compute each node's location. Nodes passed into this function should have their width and height set properly in order for layouting to work. The root node will be positioned at (0,0). */
const getElementsWithLayout = (nodes: InitialNodeData[], edges: InitialEdgeData[]) => {
  const graph = new dagre.graphlib.Graph();
  graph.setDefaultEdgeLabel(() => ({}));
  graph.setGraph({ rankdir: 'TB', ranksep: GRAPH_ROW_SPACING, nodesep: GRAPH_COLUMN_SPACING });

  const rootNodeFromInput = nodes.find(node => !edges.some(edge => edge.target === node.id));

  edges.forEach(edge => {
    graph.setEdge(edge.source, edge.target, { minlen: edge.data.minlen, edgeOrder: edge.data.edgeIndex });
  });

  nodes.forEach(node => {
    graph.setNode(node.id, { width: node.width, height: node.height });
  });

  const customOrder = (g: dagre.graphlib.Graph) => setNodeOrderWithinRanks(g, edges);
  dagre.layout(graph, { customOrder });

  const { x: initialRootNodePositionX, y: initialRootNodePositionY } = getInitialRootNodePosition(
    rootNodeFromInput,
    graph,
  );

  const layoutedNodes: LayoutedNode[] = nodes.map(node => {
    const { x: nodeCenterX, y: nodeCenterY } = graph.node(node.id);
    const { width, height } = node;

    // Calculate the node's top-left position in Dagre's coordinate system
    const nodeDagreTopLeftX = nodeCenterX - width / 2;
    const nodeDagreTopLeftY = nodeCenterY - height / 2;

    // Offset the node's position relative to the root node's Dagre-calculated top-left position.
    // This makes the root node's final top-left position (0,0).
    const finalPosition = {
      x: nodeDagreTopLeftX - initialRootNodePositionX,
      y: nodeDagreTopLeftY - initialRootNodePositionY,
    };

    return { ...node, position: finalPosition };
  });

  const finalRootNode = rootNodeFromInput ? layoutedNodes.find(n => n.id === rootNodeFromInput.id) : undefined;

  const clientCenter = getClientCenter(finalRootNode);

  return { nodes: layoutedNodes, edges, clientCenter };
};

export type ClientCenter = {
  x: number;
  y: number;
  zoom: number;
};

const getClientCenter = (rootNode?: LayoutedNode): ClientCenter => {
  if (!rootNode) {
    return { x: 0, y: 0, zoom: 0.6 };
  }
  const offset = {
    x: LAYOUT_X_OFFSET_FACTOR * (typeof document !== 'undefined' ? document.body.clientWidth : 0),
    y: LAYOUT_Y_OFFSET_FACTOR * (typeof document !== 'undefined' ? document.body.clientHeight : 0),
  };

  return {
    x: rootNode.position.x + offset.x,
    y: rootNode.position.y + offset.y,
    zoom: 1,
  };
};

export default getElementsWithLayout;
